/**
 * RepeatableInstanceRow — table row with overflow menu (#1154).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
  Dimensions,
} from 'react-native';

import { getInstanceNamePrimaryDisplay } from './repeatableSectionUtils';
import { repeatableColumnCellStyle } from './repeatableTableColumnLayout';
import { colors, spacing } from '../../theme';
import { MoreHorizontal, Copy, Trash2, GripVertical, Edit } from '../icons';
import {
  RecordTableHeader,
  RECORD_TABLE_ACTIONS_COLUMN_WIDTH,
  recordTableStyles,
  repeatableCompactActionsColumnReserved,
  repeatableRowDragState,
  repeatableRowMenuState,
  useTableRowDragAndDrop,
} from '../recordTable';

import type { RepeatableInstance, RepeatableTableLayout } from './types';

const ROW_MENU_MIN_WIDTH = 180;
const ACTION_EDIT = 'Edit';
const ACTION_DUPLICATE = 'Duplicate';
const ACTION_DELETE = 'Delete';

export interface RepeatableTableColumnAlign {
  [column: string]: 'left' | 'center';
}

export interface RepeatableInstanceRowProps<T extends RepeatableInstance> {
  instance: T;
  entityPrefix: string;
  columns: readonly string[];
  columnAlign?: RepeatableTableColumnAlign;
  headerLabel: (column: string, layout: RepeatableTableLayout) => string;
  formatCell: (
    instance: T,
    column: string,
    layout: RepeatableTableLayout,
  ) => string;
  getColumnSubline?: (
    instance: T,
    column: string,
    layout: RepeatableTableLayout,
  ) => string | undefined;
  columnUsesDoubleLine?: (
    column: string,
    layout: RepeatableTableLayout,
  ) => boolean;
  onPress?: (instance: T) => void;
  onEdit?: (instance: T) => void;
  onDuplicate?: (instance: T) => void;
  onDelete?: (instance: T) => void;
  disabled?: boolean;
  readOnly?: boolean;
  /** Devices-only: expose non-mutating View in the overflow menu. */
  allowReadOnlyOpen?: boolean;
  isPlaceholder?: boolean;
  isLastInTable?: boolean;
  testID?: string;
  reorderEnabled?: boolean;
  rowIndex?: number;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragHandleStart?: (rowIndex: number) => void;
  onDragHandleMove?: (pageY: number) => void;
  onDragHandleEnd?: () => void;
  onRowDragOver?: (rowIndex: number) => void;
  onRowDrop?: (rowIndex: number) => void;
  onRowLayout?: (
    rowIndex: number,
    bounds: { top: number; bottom: number },
  ) => void;
  tableLayout?: RepeatableTableLayout;
}

function colAlign(
  column: string,
  map?: RepeatableTableColumnAlign,
): 'left' | 'center' {
  return map?.[column] ?? 'left';
}

function RowOverflowMenu({
  testID,
  actionsLabel,
  canEdit,
  canDuplicate,
  canDelete,
  disabled,
  onEdit,
  onDuplicate,
  onDelete,
  stopRowActivation,
  openActionLabel,
}: {
  testID?: string;
  actionsLabel: string;
  canEdit: boolean;
  canDuplicate: boolean;
  canDelete: boolean;
  disabled: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  stopRowActivation?: (event?: { stopPropagation?: () => void }) => void;
  openActionLabel: string;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<View>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const close = () => setMenuOpen(false);
  const run = (fn: () => void) => {
    stopRowActivation?.();
    close();
    fn();
  };

  const openMenu = () => {
    if (disabled) return;
    setMenuOpen(true);
    anchorRef.current?.measureInWindow?.((x, y, w, h) => {
      setPos({ top: y + h + 4, left: x + w - ROW_MENU_MIN_WIDTH });
    });
  };

  useEffect(() => {
    if (!menuOpen || Platform.OS !== 'web' || typeof document === 'undefined')
      return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      close();
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [menuOpen]);

  const items = [
    canEdit ? { key: 'edit', label: openActionLabel, onPress: onEdit } : null,
    canDuplicate
      ? { key: 'dup', label: ACTION_DUPLICATE, onPress: onDuplicate }
      : null,
    canDelete
      ? {
          key: 'del',
          label: ACTION_DELETE,
          onPress: onDelete,
          destructive: true,
        }
      : null,
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    onPress: () => void;
    destructive?: boolean;
  }>;
  const accessibilityLabel =
    items.length === 1 && items[0]?.label === 'View'
      ? `View ${actionsLabel}`
      : `More actions for ${actionsLabel}`;

  if (items.length === 0) {
    return (
      <View style={styles.menuAnchor}>
        <Pressable
          testID={testID ? `${testID}-menu` : undefined}
          style={[styles.menuButton, styles.menuButtonDisabled]}
          disabled
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
          accessibilityState={{ disabled: true }}
        >
          <MoreHorizontal size={18} color={colors.textMuted} />
        </Pressable>
      </View>
    );
  }

  return (
    <View ref={anchorRef} style={styles.menuAnchor}>
      <Pressable
        testID={testID ? `${testID}-menu` : undefined}
        style={[styles.menuButton, disabled && styles.menuButtonDisabled]}
        onPress={e => {
          stopRowActivation?.(e);
          openMenu();
        }}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ disabled }}
      >
        <MoreHorizontal size={18} color={colors.textSecondary} />
      </Pressable>
      {Platform.OS === 'web' && menuOpen && typeof document !== 'undefined'
        ? require('react-dom').createPortal(
            <div
              data-testid={testID ? `${testID}-menu-dropdown` : undefined}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                minWidth: ROW_MENU_MIN_WIDTH,
                zIndex: 999999,
                background: colors.backgroundPrimary,
                border: `1px solid ${colors.borderCard}`,
                borderRadius: 8,
                padding: '4px 0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
              onMouseDown={e => e.stopPropagation()}
            >
              {items.map(item => (
                <button
                  key={item.key}
                  type="button"
                  data-testid={
                    testID ? `${testID}-menu-${item.key}` : undefined
                  }
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: item.destructive ? colors.error : colors.textPrimary,
                  }}
                  onClick={() => run(item.onPress)}
                >
                  {item.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
      {Platform.OS !== 'web' ? (
        <Modal
          visible={menuOpen}
          transparent
          animationType="fade"
          onRequestClose={close}
        >
          <Pressable style={styles.modalBackdrop} onPress={close}>
            <View style={styles.nativeMenu}>
              {items.map(item => (
                <TouchableOpacity
                  key={item.key}
                  style={styles.menuItem}
                  onPress={() => run(item.onPress)}
                >
                  <Text
                    style={[
                      styles.menuItemText,
                      item.destructive && styles.menuItemDestructive,
                    ]}
                  >
                    {item.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </Pressable>
        </Modal>
      ) : null}
    </View>
  );
}

export function RepeatableInstanceTableHeader({
  columns,
  columnAlignMap,
  headerLabel,
  reorderEnabled = false,
  showDragColumn = false,
  tableLayout = 'desktop',
  readOnly = false,
  hasOpenAction = false,
}: {
  columns: readonly string[];
  columnAlignMap?: RepeatableTableColumnAlign;
  headerLabel: (column: string, layout: RepeatableTableLayout) => string;
  reorderEnabled?: boolean;
  showDragColumn?: boolean;
  tableLayout?: RepeatableTableLayout;
  readOnly?: boolean;
  hasOpenAction?: boolean;
}): React.ReactElement {
  return (
    <RecordTableHeader
      columns={columns}
      getColumnAlign={column => colAlign(column, columnAlignMap)}
      getHeaderLabel={column => headerLabel(column, tableLayout)}
      getColumnCellStyle={(column, layout) =>
        repeatableColumnCellStyle(column, layout)
      }
      reorderEnabled={reorderEnabled}
      showDragColumn={showDragColumn}
      tableLayout={tableLayout}
      readOnly={readOnly}
      hasOpenAction={hasOpenAction}
    />
  );
}

export function RepeatableInstanceRow<T extends RepeatableInstance>(
  props: RepeatableInstanceRowProps<T>,
): React.ReactElement {
  const {
    instance,
    entityPrefix,
    columns,
    columnAlign: columnAlignMap,
    formatCell,
    getColumnSubline,
    columnUsesDoubleLine,
    onPress,
    onEdit,
    onDuplicate,
    onDelete,
    disabled = false,
    readOnly = false,
    allowReadOnlyOpen = false,
    isPlaceholder = false,
    isLastInTable = false,
    testID,
    reorderEnabled = false,
    rowIndex,
    isDragging = false,
    isDragOver = false,
    onDragHandleStart,
    onDragHandleMove,
    onDragHandleEnd,
    onRowDragOver,
    onRowDrop,
    onRowLayout,
    registerRowRemeasure,
    tableLayout = 'desktop',
  } = props;

  const isCompact = tableLayout === 'compact';
  const isExisting = instance.instanceStatus === 'existing';
  const openEditor = onEdit ?? onPress;
  const canEdit =
    Boolean(openEditor) &&
    !disabled &&
    (!readOnly || allowReadOnlyOpen) &&
    !isPlaceholder;
  const canDuplicate =
    Boolean(onDuplicate) && !disabled && !readOnly && !isPlaceholder;
  const canDelete =
    Boolean(onDelete) && !disabled && !readOnly && !isPlaceholder;
  const { showOverflowMenu: showMenu, menuDisabled } = repeatableRowMenuState({
    disabled,
    readOnly,
    isPlaceholder,
    canEdit,
    canDuplicate,
    canDelete,
  });
  const displayName = getInstanceNamePrimaryDisplay(instance, entityPrefix);
  const stopRowActivation = useCallback(
    (event?: { stopPropagation?: () => void }) => event?.stopPropagation?.(),
    [],
  );

  const { showDragHandle, dragHandleActive } = repeatableRowDragState({
    reorderEnabled,
    disabled,
    readOnly,
    isPlaceholder,
    rowIndex,
  });
  const dragHandleRef = useRef<View>(null);
  const dropRowRef = useRef<View>(null);

  const { nativeDragHandleProps, onDropRowLayout } = useTableRowDragAndDrop({
    enabled: dragHandleActive,
    rowIndex: rowIndex ?? 0,
    dragHandleRef,
    dropRowRef,
    onDragHandleStart,
    onDragHandleMove,
    onDragHandleEnd,
    onRowDragOver,
    onRowDrop,
    onRowLayout,
    registerRowRemeasure,
  });

  const dragHandleCell = showDragHandle ? (
    <View
      ref={dragHandleRef}
      testID={testID ? `${testID}-drag-handle` : undefined}
      style={[
        styles.dragHandleCell,
        !dragHandleActive && styles.dragHandleDisabled,
      ]}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      pointerEvents="box-only"
      accessibilityRole="button"
      accessibilityLabel="Drag to reorder"
      accessibilityState={{ disabled: !dragHandleActive }}
      {...nativeDragHandleProps}
    >
      <GripVertical size={16} color={colors.textMuted} />
    </View>
  ) : null;

  const statusBar = (
    <View
      style={[
        styles.statusBar,
        isExisting ? styles.statusExisting : styles.statusNew,
      ]}
    />
  );

  const cells = (
    <>
      {columns.map(col => {
        const value = isPlaceholder
          ? '—'
          : formatCell(instance, col, tableLayout);
        const isEmptyCell = value === '—';
        const subline =
          !isPlaceholder && getColumnSubline
            ? getColumnSubline(instance, col, tableLayout)
            : undefined;
        const doubleLine =
          columnUsesDoubleLine?.(col, tableLayout) && Boolean(subline);
        const isCentered = colAlign(col, columnAlignMap) === 'center';
        return (
          <View
            key={col}
            style={[
              isCompact
                ? recordTableStyles.compactTableCell
                : recordTableStyles.tableCell,
              isCentered
                ? recordTableStyles.tableCellCenter
                : recordTableStyles.tableCellStart,
              repeatableColumnCellStyle(col, tableLayout),
            ]}
          >
            {doubleLine ? (
              <>
                <Text
                  style={[
                    recordTableStyles.modelPrimaryText,
                    isCompact && recordTableStyles.compactPrimaryText,
                    isPlaceholder && recordTableStyles.placeholderText,
                    isEmptyCell && recordTableStyles.placeholderMuted,
                  ]}
                  numberOfLines={1}
                >
                  {value}
                </Text>
                <Text
                  style={[
                    recordTableStyles.modelSublineText,
                    isCompact && recordTableStyles.compactSublineText,
                  ]}
                  numberOfLines={1}
                >
                  {subline}
                </Text>
              </>
            ) : (
              <Text
                style={[
                  recordTableStyles.tableCellText,
                  isCompact && recordTableStyles.compactCellText,
                  isCentered && recordTableStyles.tableCellTextCenter,
                  (isPlaceholder || isEmptyCell) &&
                    recordTableStyles.placeholderMuted,
                ]}
                numberOfLines={isCompact && !isCentered ? 2 : 1}
              >
                {value}
              </Text>
            )}
          </View>
        );
      })}
    </>
  );

  // Collapse the empty actions column on native compact read-only lists (Devices
  // browse) so the data columns reclaim the space; web is unchanged (#999).
  const showCompactActionsColumn = repeatableCompactActionsColumnReserved({
    isCompact,
    readOnly,
    hasOpenAction: Boolean(openEditor) && (!readOnly || allowReadOnlyOpen),
  });
  const actions = (
    <View
      style={[
        styles.actionsCell,
        isCompact &&
          (showCompactActionsColumn
            ? styles.actionsCellCompact
            : styles.actionsHidden),
      ]}
    >
      {showMenu ? (
        <RowOverflowMenu
          testID={testID}
          actionsLabel={displayName}
          canEdit={canEdit}
          canDuplicate={canDuplicate}
          canDelete={canDelete}
          disabled={menuDisabled}
          onEdit={() => openEditor?.(instance)}
          onDuplicate={() => onDuplicate?.(instance)}
          onDelete={() => onDelete?.(instance)}
          stopRowActivation={stopRowActivation}
          openActionLabel={readOnly ? 'View' : ACTION_EDIT}
        />
      ) : null}
    </View>
  );

  const shell = [
    styles.row,
    isLastInTable && styles.rowLast,
    disabled && styles.rowDisabled,
    isDragging && styles.rowDragging,
    isDragOver && styles.rowDragOver,
  ];

  if (!onPress || disabled) {
    return (
      <View
        ref={dropRowRef}
        testID={testID}
        style={shell}
        onLayout={onDropRowLayout}
      >
        {statusBar}
        {dragHandleCell}
        {cells}
        {actions}
      </View>
    );
  }

  return (
    <View ref={dropRowRef} style={shell} onLayout={onDropRowLayout}>
      {statusBar}
      {dragHandleCell}
      <Pressable
        testID={testID}
        style={styles.rowPressable}
        onPress={() => onPress(instance)}
        accessibilityRole="button"
        accessibilityLabel={displayName}
      >
        {cells}
      </Pressable>
      {actions}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottomWidth: 1,
    borderBottomColor: colors.borderCard,
    minHeight: 56,
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({
      web: {
        width: '100%',
        alignSelf: 'stretch',
      },
    }),
  },
  rowLast: { borderBottomWidth: 0 },
  rowDisabled: { opacity: 0.6 },
  rowDragging: { opacity: 0.45 },
  rowDragOver: { backgroundColor: colors.surfaceAccentMuted },
  rowPressable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    minWidth: 0,
  },
  statusBar: { width: 5, flexShrink: 0 },
  statusExisting: { backgroundColor: colors.success },
  statusNew: { backgroundColor: colors.primary },
  dragHandleCell: {
    width: 28,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab' as unknown as undefined,
  },
  dragHandleDisabled: {
    opacity: 0.4,
    cursor: 'default' as unknown as undefined,
  },
  actionsCell: {
    width: RECORD_TABLE_ACTIONS_COLUMN_WIDTH,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  actionsCellCompact: {
    width: 36,
  },
  actionsHidden: { width: 0, display: 'none' as unknown as undefined },
  menuAnchor: { position: 'relative', zIndex: 20 },
  menuButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.borderCard,
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuButtonDisabled: {
    opacity: 0.45,
  },
  menuPlaceholder: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.35,
  },
  menuItem: { paddingVertical: spacing.sm, paddingHorizontal: spacing.md },
  menuItemText: { fontSize: 14, color: colors.textPrimary },
  menuItemDestructive: { color: colors.error },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.2)',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  nativeMenu: {
    backgroundColor: colors.backgroundPrimary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.borderCard,
    paddingVertical: spacing.xs,
  },
});
